/**
 * Chord symbol → the notes it names.
 *
 * The song pages show chord boxes that say "click a chord to hear it", but a box
 * only had a sound if someone had typed its notes by hand. Most never were, so
 * most boxes were silent. This reads the symbol itself ("Em/G", "Asus2sus4",
 * "C♭m7", "F#ø7") and returns a playable voicing, so every box sounds exactly
 * what its name says. Anything it cannot read returns null and stays silent —
 * a wrong chord is worse than no chord.
 */

const LETTER_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

function notePc(s: string): number | null {
  const m = /^([A-G])((?:#|b|♯|♭|x)*)$/.exec(s);
  if (!m) return null;
  let pc = LETTER_PC[m[1]];
  for (const a of m[2]) pc += a === "#" || a === "♯" ? 1 : a === "x" ? 2 : -1;
  return ((pc % 12) + 12) % 12;
}

export interface ParsedChord {
  rootPc: number;
  bassPc: number;
  /** Semitones above the root, ascending, root (0) included. */
  intervals: number[];
}

/** Read a chord symbol. Returns null for anything it does not fully understand. */
export function parseChordSymbol(symbol: string): ParsedChord | null {
  let s = symbol.trim().replace(/♯/g, "#").replace(/♭/g, "b").replace(/\s/g, "").replace(/–/g, "-");
  // A stack written as notes, e.g. "D-G-C" (quartal voicings), optionally over a
  // bass note: "G-C-F/D".
  const stack = /^([A-G](?:#|b)?(?:-[A-G](?:#|b)?)+)(?:\/([A-G](?:#|b)?))?$/.exec(s);
  if (stack) {
    const pcs = stack[1].split("-").map(notePc);
    if (pcs.some((p) => p == null)) return null;
    const root = pcs[0] as number;
    let prev = 0;
    const intervals = pcs.map((p) => { let iv = ((p as number) - root + 12) % 12; while (iv < prev) iv += 12; prev = iv; return iv; });
    const bass = stack[2] ? notePc(stack[2]) : root;
    return bass == null ? null : { rootPc: root, bassPc: bass, intervals };
  }
  const rm = /^([A-G](?:#|b)?)/.exec(s);
  if (!rm) return null;
  const rootPc = notePc(rm[1]);
  if (rootPc == null) return null;
  // Parentheses group an alteration ("B(b5)", "Cm(maj7)"); a bracketed note name
  // is an enharmonic aside ("Ab(G#)") and names no extra chord tone.
  s = s.slice(rm[1].length).replace(/\([A-G](?:#|b)?\)$/, "").replace(/[()]/g, "");

  // Slash bass — but "6/9" is a chord, not a bass note.
  let bassPc = rootPc;
  const sm = /\/([A-G](?:#|b)?)$/.exec(s);
  if (sm && !/6\/9$/.test(s)) {
    const b = notePc(sm[1]);
    if (b == null) return null;
    bassPc = b;
    s = s.slice(0, sm.index);
  }

  let third: number | null = 4;
  let fifth: number | null = 7;
  let seventh: number | null = null;
  const extra = new Set<number>();

  s = s.replace(/Δ/g, "maj").replace(/ø7?/g, "m7b5").replace(/°7/g, "dim7").replace(/°/g, "dim").replace(/^-/, "m");

  if (/^(m|min)(?!aj)/.test(s)) { third = 3; s = s.replace(/^(min|m)/, ""); }
  else if (/^dim/.test(s)) {
    third = 3; fifth = 6; s = s.slice(3);
    if (s.startsWith("7")) { seventh = 9; s = s.slice(1); }
  } else if (/^(aug|\+)/.test(s)) { fifth = 8; s = s.replace(/^(aug|\+)/, ""); }
  else if (/^5(?!\d)/.test(s) && !s.startsWith("5/")) { third = null; s = s.slice(1); }

  const TOKENS: [RegExp, () => void][] = [
    [/^(maj|Maj|M)13/, () => { seventh = 11; extra.add(14); extra.add(21); }],
    [/^(maj|Maj|M)11/, () => { seventh = 11; extra.add(14); extra.add(17); }],
    [/^(maj|Maj|M)9/, () => { seventh = 11; extra.add(14); }],
    [/^(maj|Maj|M)7?/, () => { seventh = 11; }],
    [/^6\/9|^69/, () => { extra.add(9); extra.add(14); }],
    [/^13/, () => { seventh = seventh ?? 10; extra.add(14); extra.add(21); }],
    [/^11/, () => { seventh = seventh ?? 10; extra.add(14); extra.add(17); }],
    [/^9/, () => { seventh = seventh ?? 10; extra.add(14); }],
    [/^7/, () => { seventh = seventh ?? 10; }],
    [/^6/, () => { extra.add(9); }],
    [/^add(2|9)/, () => { extra.add(14); }],
    [/^add(4|11)/, () => { extra.add(17); }],
    [/^add(6|13)/, () => { extra.add(9); }],
    [/^sus2/, () => { third = null; extra.add(2); }],
    [/^sus4?/, () => { third = null; extra.add(5); }],
    [/^(b5|-5)/, () => { fifth = 6; }],
    [/^(#5|\+5)/, () => { fifth = 8; }],
    [/^b9/, () => { extra.add(13); }],
    [/^#9/, () => { extra.add(15); }],
    [/^#11/, () => { extra.add(18); }],
    [/^b13/, () => { extra.add(20); }],
    [/^(no3|omit3)/, () => { third = null; }],
    [/^(no5|omit5)/, () => { fifth = null; }],
  ];
  let guard = 0;
  while (s.length && guard++ < 12) {
    const hit = TOKENS.find(([re]) => re.test(s));
    if (!hit) return null;
    const m = hit[0].exec(s)!;
    hit[1]();
    s = s.slice(m[0].length);
  }
  if (s.length) return null;

  const iv = new Set<number>([0]);
  if (third != null) iv.add(third);
  if (fifth != null) iv.add(fifth);
  if (seventh != null) iv.add(seventh);
  extra.forEach((e) => iv.add(e));
  return { rootPc, bassPc, intervals: [...iv].sort((a, b) => a - b) };
}

/**
 * A plain piano voicing: the bass note low (E2–D#3), the chord stacked in close
 * position from the root in the C3–B3 octave. Returned as sharp-spelled pitch names
 * for the sampler — this is for SOUND only; on-screen spelling stays as written.
 */
export function chordSymbolToPitches(symbol: string): string[] | null {
  const c = parseChordSymbol(symbol);
  if (!c) return null;
  const rootMidi = 48 + c.rootPc;
  const bassMidi = 40 + ((c.bassPc - 4 + 12) % 12);
  const midis = [bassMidi, ...c.intervals.map((i) => rootMidi + i)];
  const uniq = [...new Set(midis)].sort((a, b) => a - b);
  return uniq.map((m) => `${SHARP_NAMES[m % 12]}${Math.floor(m / 12) - 1}`);
}
